import { cn } from "@/libraries/cn";
import { allFileExtensionToMime } from "@/libraries/constant";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import IconButton from "@mui/material/IconButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { fileService } from "../../services/file";

interface MediaThumbnailProps extends React.ComponentPropsWithoutRef<"div"> {
	src: string;
	mimeType?: string;
}

const MediaThumbnail: React.FC<MediaThumbnailProps> = ({
	src,
	mimeType: originalMimeType,
	className,
	...props
}) => {
	const [thumbnail, setThumbnail] = useState<string | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement>(document.createElement("canvas"));

	const mimeType = useMemo(() => {
		if (originalMimeType) return originalMimeType;
		const ext = fileService.getExtension(src);
		if (!ext) return undefined;
		return allFileExtensionToMime[ext];
	}, [src, originalMimeType]);

	const isVideo = mimeType?.startsWith("video/");
	const isImage = mimeType?.startsWith("image/");

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!isVideo) return;

		const video = videoRef.current;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");

		if (!video || !ctx) return;

		const captureThumbnail = () => {
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
			setThumbnail(canvas.toDataURL("image/png"));
		};

		video.addEventListener("loadeddata", captureThumbnail);
		return () => video.removeEventListener("loadeddata", captureThumbnail);
	}, [src, isVideo]);

	return (
		<div className={cn("relative", className)} {...props}>
			{isImage ? (
				<img
					src={src}
					alt="Thumbnail"
					className="object-cover object-center w-full h-full"
				/>
			) : isVideo ? (
				<>
					{thumbnail ? (
						<img
							src={thumbnail}
							alt="Video thumbnail"
							className="object-cover object-center w-full h-full"
						/>
					) : (
						<video
							ref={videoRef}
							playsInline
							muted
							className="object-cover object-center w-full h-full"
						>
							<source src={src} type={mimeType} />
							Your browser does not support the video tag.
						</video>
					)}
					<IconButton
						size="small"
						className="!absolute !top-1/2 !left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white"
					>
						<PlayArrowIcon />
					</IconButton>
				</>
			) : (
				<div className="text-center text-sm text-gray-500">
					Unsupported media
				</div>
			)}
		</div>
	);
};

export default MediaThumbnail;
